import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'

import type { ShopPostAction } from '@/lib/api/shop-cms-api'
import type {
    ShopPageInput,
    ShopPageListParams,
} from '@/lib/api/shop-pages-api'
import { ApiError } from '@/lib/api/client'
import { shopPagesApi } from '@/lib/api/shop-pages-api'

export const shopPageKeys = {
    lists: ['shop-pages'] as const,
    list: (p: ShopPageListParams) => ['shop-pages', p] as const,
    infinite: (p: Omit<ShopPageListParams, 'page'>) =>
        ['shop-pages', 'infinite', p] as const,
    detail: (id: string) => ['shop-page', id] as const,
}

export function useShopPages(
    params: ShopPageListParams,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: shopPageKeys.list(params),
        queryFn: () => shopPagesApi.pages(params),
        placeholderData: keepPreviousData,
        enabled: options?.enabled ?? true,
    })
}

const PICKER_PER_PAGE = 20

/** Ô chọn trang / câu chuyện: tìm trên server (debounce ở màn), cuộn tới đáy tải trang sau. */
export function useShopPagesInfinite(
    params: Omit<ShopPageListParams, 'page' | 'per_page'>,
) {
    const query = { ...params, per_page: PICKER_PER_PAGE }
    return useInfiniteQuery({
        queryKey: shopPageKeys.infinite(query),
        queryFn: ({ pageParam }) =>
            shopPagesApi.pages({ ...query, page: pageParam }),
        // Thiếu quyền (403) thì báo lỗi ngay để ô chọn đổi sang ô nhập, không
        // thử lại; lỗi khác thử lại 1 lần như mặc định (lib/query-client.ts).
        retry: (failures, err) =>
            !(err instanceof ApiError && err.status === 403) && failures < 1,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useShopPage(id: string | undefined) {
    return useQuery({
        queryKey: shopPageKeys.detail(id ?? ''),
        queryFn: () => shopPagesApi.page(id!),
        enabled: !!id,
    })
}

export type ShopPageSave =
    | { kind: 'create'; input: ShopPageInput }
    | { kind: 'save'; id: string; input: ShopPageInput }
    | { kind: ShopPostAction; id: string; input: ShopPageInput }

/** Mọi thao tác ghi trang: cập nhật cache trang vừa lưu. */
export function useSaveShopPage() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (s: ShopPageSave) => {
            switch (s.kind) {
                case 'create':
                    return shopPagesApi.createPage(s.input)
                case 'save':
                    return shopPagesApi.savePage(s.id, s.input)
                default:
                    return shopPagesApi.pageAction(s.id, s.kind, s.input)
            }
        },
        onSuccess: (res) => {
            if (res.data) qc.setQueryData(shopPageKeys.detail(res.data.id), res)
            void qc.invalidateQueries({ queryKey: shopPageKeys.lists })
        },
    })
}

export function useDeleteShopPage() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shopPagesApi.deletePage(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: shopPageKeys.lists }),
    })
}
